import { useEffect, useState } from "react";
import api, { formatAxiosError } from "@/lib/api";
import { Gear, FloppyDisk, Timer } from "@phosphor-icons/react";

export default function GeneralSettingsTab() {
  const [cfg, setCfg] = useState({ session_timeout_minutes: 0 });
  const [initial, setInitial] = useState({ session_timeout_minutes: 0 });
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState({ type: "", text: "" });

  const flash = (type, text) => {
    setMsg({ type, text });
    setTimeout(() => setMsg({ type: "", text: "" }), 4000);
  };

  const load = async () => {
    try {
      const r = await api.get("/settings/general");
      setCfg(r.data);
      setInitial(r.data);
    } catch (e) {
      flash("error", formatAxiosError(e));
    }
  };

  useEffect(() => { load(); }, []);

  const dirty = JSON.stringify(cfg) !== JSON.stringify(initial);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        session_timeout_minutes: Math.max(0, parseInt(cfg.session_timeout_minutes || 0, 10)),
      };
      const r = await api.put("/settings/general", payload);
      setCfg(r.data); setInitial(r.data);
      flash("ok", "General settings saved.");
    } catch (e) {
      flash("error", formatAxiosError(e));
    } finally {
      setSaving(false);
    }
  };

  const val = parseInt(cfg.session_timeout_minutes || 0, 10);

  return (
    <div className="max-w-3xl space-y-8">
      {msg.text && (
        <div className={`text-xs font-mono-mini uppercase tracking-widest px-3 py-2 border ${msg.type === "ok" ? "border-green-500 text-green-700 bg-green-50" : "border-red-500 text-red-700 bg-red-50"}`}>
          {msg.text}
        </div>
      )}

      <section className="border border-border p-6">
        <div className="flex items-start gap-3 mb-4">
          <Timer size={20} weight="bold" className="text-[#002FA7] mt-1" />
          <div>
            <h3 className="font-display text-xl font-black mb-1">Session timeout</h3>
            <p className="text-sm text-muted-foreground">
              Automatically sign a user out after N minutes of inactivity. Enter <b>0</b> to disable
              (users stay signed in until their access token expires).
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3 mt-6">
          <input
            type="number"
            min={0}
            max={43200}
            value={cfg.session_timeout_minutes ?? 0}
            onChange={(e) => setCfg({ ...cfg, session_timeout_minutes: e.target.value })}
            className="w-32 border border-border px-3 py-2 text-sm"
            data-testid="general-session-timeout"
          />
          <div className="text-sm text-muted-foreground">minutes {val === 0 ? "(no timeout)" : ""}</div>
        </div>
        <div className="text-[10px] font-mono-mini text-muted-foreground mt-3">
          Applies globally to all users. Enforcement runs on every authenticated API call.
        </div>
      </section>

      <div className="flex items-center gap-2">
        <button onClick={load} disabled={!dirty} className="border border-border px-3 py-2 text-sm hover:bg-muted disabled:opacity-40">
          Reset
        </button>
        <button
          onClick={save}
          disabled={!dirty || saving}
          className="btn-primary flex items-center gap-2 disabled:opacity-40"
          data-testid="save-general-settings"
        >
          <FloppyDisk size={14} weight="bold" /> {saving ? "Saving…" : "Save"}
        </button>
      </div>
    </div>
  );
}
